import React, { type Dispatch, type FC, type SetStateAction } from 'react';
import { Portal } from 'react-portal';
import type { TrackingProp } from 'react-tracking';
import type { FormValues } from '@ynap/form';
import { useFormField } from '@ynap/form';
import Overlay, { StopPropagation } from '@ynap/overlay';
import { AddressForm } from '../AddressForm';
import { mapAddressValues } from '../AddressForm/utils';
import type { AddAddressAction } from './@types';
import type { Locale, Config, Messages, CheckoutAnalytics } from '../AddressForm/@types';

type AnyFn = (...args: any[]) => any;

interface AddAddressRouteProps {
    config: Config;
    addTitle: string;
    locale: Locale;
    history: { push: (url: string) => void };
    messages: Messages;
    match: { path: string; url: string };
    addAddress?: AddAddressAction;
    trackConfirmAddNewAddress?: () => void;
    withOverlay?: boolean;
    isCheckoutLayout?: boolean;
    formTitle?: string;
    setEditAddressID?: AnyFn;
    onSubmit?: AnyFn;
    hideCountry?: boolean;
    tracking?: TrackingProp;
    primaryCategory?: string;
    checkoutAnalytics?: CheckoutAnalytics;
    removeAddressFinder?: (...args: any[]) => any;
    setRemoveAddressFinder?: Dispatch<SetStateAction<() => any>>;
}

const AddAddressRoute: FC<AddAddressRouteProps> = ({
    addAddress,
    addTitle,
    checkoutAnalytics,
    config,
    formTitle,
    hideCountry,
    history,
    isCheckoutLayout,
    locale,
    match,
    messages,
    onSubmit,
    primaryCategory,
    removeAddressFinder,
    setEditAddressID,
    setRemoveAddressFinder,
    trackConfirmAddNewAddress,
    tracking,
    withOverlay = true,
}) => {
    const [addressIdField, setAddressIdField] = useFormField('addressId');

    const handleSubmit = (values: FormValues) => {
        if (addAddress) {
            const mappedValues = mapAddressValues(values, locale);
            trackConfirmAddNewAddress?.();
            return addAddress(mappedValues).then(({ payload: { addressId } }) => {
                // Need to update the currently selected address
                setEditAddressID?.('');
                setAddressIdField({ ...addressIdField, value: addressId });

                if (!isCheckoutLayout) {
                    history.push(match.url);
                } else {
                    onSubmit?.({ ...values, addressId });
                }
            });
        }
    };

    const renderContent = () => (
        <AddressForm
            checkoutAnalytics={checkoutAnalytics}
            config={config}
            country={locale.country}
            formTitle={formTitle}
            hideCountry={hideCountry}
            isCheckoutLayout={isCheckoutLayout}
            messages={messages}
            onSubmit={withOverlay || isCheckoutLayout ? handleSubmit : undefined}
            primaryCategory={primaryCategory}
            removeAddressFinder={removeAddressFinder}
            setRemoveAddressFinder={setRemoveAddressFinder}
            tracking={tracking}
        />
    );

    if (withOverlay) {
        return (
            <Portal>
                <StopPropagation>
                    <Overlay onClose={() => history.push(match.url)} title={addTitle}>
                        {renderContent()}
                    </Overlay>
                </StopPropagation>
            </Portal>
        );
    }

    return renderContent();
};

export default AddAddressRoute;
